import {api} from "../api";
import {BulkSelectorContextMenu} from "@/shared/ui/bulkSelectorContextMenu";
import {htmlEscape} from "../utils/string";
import {ElementSelector, isAccessToRecordAllowed, saveJQuerySelector} from "@/shared/utils/misc";
import {formatTime, getISODate, parseDateInput, parseISOZonedDateTime} from "@/shared/utils/datefunctions";
import {
	MandatoryValidationsResponse,
	StartEndTime,
	ValidationData,
	ValidationMessage,
	ValidationResultRecord
} from "@/shared/timr";

export function setRecordingValidationLinkIconLoading(link: ElementSelector) {
	link = saveJQuerySelector(link);
	link.prop("disabled", true);

	const icon = link.find("i.time-separation-symbol");
	icon.next("i.fa-spinner-third").remove();
	icon.hide();
	icon.after(`<i class='far fa-spinner-third spinner ml-5 mr-5'></i>`);
}

export function resetRecordingValidationLinkIconLoading(link: ElementSelector) {
	link = saveJQuerySelector(link);
	link.prop("disabled", false);

	const icon = link.find("i.time-separation-symbol");
	icon.next("i.fa-spinner-third").remove();
	icon.show();
}

export function setRecordingValidationsTableLoadingState(setLoading: boolean) {
	if (setLoading) {
		$("#recordingValidationsListCollapse").collapse("hide");
		$(".reload-table").hide();
		$(".recordingValidationsProgressTableIndicator").show();
	} else {
		$(".reload-table").show();
		$(".recordingValidationsProgressTableIndicator").hide();
	}
}

export function formatRecordedEntryForValidationMessage(recordedEntry: ValidationResultRecord, showTimeZone: boolean) {
	let startTimeZone = "";
	let endTimeZone = "";
	if (showTimeZone) {
		startTimeZone = parseISOZonedDateTime(recordedEntry.start)?.offset ?? "";
		endTimeZone = parseISOZonedDateTime(recordedEntry.end)?.offset ?? "";
	}
	let recordClass = "recordWorkingTime";

	if (recordedEntry.task) {
		recordClass = "recordProjectTime";
	}

	return `<a class="${recordClass}" ${isAccessToRecordAllowed(recordedEntry) ? `data-id="${recordedEntry.id}"` : "data-user-trust-based"}>
			${formatTime(parseISOZonedDateTime(recordedEntry.start))} <span class="text-timezone">${startTimeZone}</span>
			<i class="time-separation-symbol ml-5 mr-5"></i>${formatTime(parseISOZonedDateTime(recordedEntry.end))} <span class="text-timezone">${endTimeZone}</span>
		</a>`;
}

function showTimeZoneForValidationMessage(rvm: ValidationMessage) {
	if (globals.showTimeZone) {
		return true;
	}
	const timeZones = [];
	if (rvm.recordedEntry) {
		timeZones.push(parseISOZonedDateTime(rvm.recordedEntry.start)?.offset ?? "");
		timeZones.push(parseISOZonedDateTime(rvm.recordedEntry.end)?.offset ?? "");
	}
	if (rvm.recordedEntryRef) {
		timeZones.push(parseISOZonedDateTime(rvm.recordedEntryRef.start)?.offset ?? "");
		timeZones.push(parseISOZonedDateTime(rvm.recordedEntryRef.end)?.offset ?? "");
	}
	return !timeZones.every((val, i, arr) => val === arr[0]);
}

/**
 * Builds the content of the validation container that is displayed on the time account pages.
 * For this purpose, a table is created which displays the date, status, user (if showUser is enabled), message of the validation
 * After that follows a link to the recordingValidation.html page
 *
 * @param validationData data containing validations
 * @param showUser if user column should be displayed
 * @param showRecordedEntries if referenced recordings should be displayed
 */
// tslint:disable-next-line:max-line-length
export function buildRecordingValidationContainerContent(validationData: ValidationData | MandatoryValidationsResponse, showUser: boolean = false, showRecordedEntries: boolean = true, contextMenu?: BulkSelectorContextMenu): JQuery {
	const validationContainer = $("<div>").addClass("recording-validation-container");
	const table = $("<table>");
	for (const validation of validationData.validationMessage) {
		let references = htmlEscape(validation.message);
		if (showRecordedEntries) {
			const showTimeZone = showTimeZoneForValidationMessage(validation);
			if (validation.recordedEntry) {
				references += formatRecordedEntryForValidationMessage(validation.recordedEntry, showTimeZone);
			}
			if (validation.recordedEntryRef) {
				references += ` ${messages["timeAccount.validationProblems.and"]} ` + formatRecordedEntryForValidationMessage(validation.recordedEntryRef, showTimeZone);
			}
		}
		if (contextMenu && (validation.messageCode === "validationResult.missingEntryOnWorkingDay" || validation.messageCode === "validationResult.missingEntryOnHalfDayHoliday")) {
			references = formatMissingWorkingTimeDropdown(references, validation, contextMenu);
		}
		const tr = $("<tr>");
		tr.append($("<td>").text(validation.weekday))
			.append($("<td>").text(validation.date?.toString() ?? ""));
		if (showUser) {
			tr.append($("<td>").text(validation.user));
		}
		if (globals.savedValidationsEnabled) {
			tr.append($("<td>").text(validation.status));
		}
		table.append(
			tr.append($("<td>").addClass("w-100").html(references))
		);
	}
	validationContainer.append(table);

	// append link referring to recording validations page
	if (globals.savedValidationsEnabled) {
		validationContainer.append($("<a>")
			.addClass("display-block mt-5")
			.attr("href", "/timr/reports/recordingValidations.html")
			.text(messages["timeAccount.validationsContainer.all"]));
	}
	return validationContainer;
}

export function formatMissingWorkingTimeDropdown(text: string, validation: ValidationMessage, contextMenu: BulkSelectorContextMenu): string {
	return $("<div>").addClass("display-flex")
		.append($("<span>").text(text).addClass("display-flex mr-20"))
		.append($("<ul>").addClass("list-context-menu")
			.append($("<li>").addClass("dropdown ")
				.append($("<a>",
					{
						"data-toggle": "dropdown",
						"aria-expanded": "false",
						"data-id": validation.id,
						"data-date": getISODate(parseDateInput(validation.date)),
						"data-userid": validation.userId
					}
				).addClass("dropdown-toggle").addClass("addTimeDropdown")
					.append($("<i>").addClass("far fa-plus position-left"))
					.append($("<span>").text(messages["recordingValidations.add"])))
				.append(contextMenu.render())
			)
		)
		.prop("outerHTML") as string;
}

export function getStartEndTimeForMissingRecordingValidation(validationId: number, userId: number, displayDate: string): JQuery.jqXHR<StartEndTime> {
	return api("reports/getStartAndEndDateTimeForMissingRecordingValidation.json")
		.get({
			validationId: validationId,
			userId: userId,
			displayDate: displayDate
		});
}
